import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { useLocalState } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/useLocalState';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import { MAX_OKR_NAME_LENGTH, MIN_OKR_NAME_LENGTH } from 'constants/ProjectConstants';

import Caption from 'uiToolkitV1/Caption';
import HeaderInput from 'uiToolkitV1/HeaderInput';

import { useOKRAccessControl } from '../AccessControl/useOKRAccessControl';

export const OKRHeaderInput = ({
  type = 'normal',
  text = '',
  onChange = () => {},
  onLocalOKRNameChange = () => {},
  isObjective = true,
  leftComponent = null,
  onFocusChange = () => {},
  isOKRPanelDisabled,
}) => {
  const { canEditOKR } = useOKRAccessControl();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const placeholder = `Add a specific and relevant ${isObjective ? objectivesName : keyResultsName}`;

  const ref = useRef();
  const [localOKRName, setLocalOKRName] = useLocalState(text);
  const [error, setError] = useState('');
  const [isFocus, setFocus] = useState(false);

  useEffect(() => {
    onLocalOKRNameChange(localOKRName);
  }, [localOKRName]);

  useEffect(() => {
    onFocusChange(isFocus);
  }, [isFocus]);

  const hasError = error.length > 0;

  const { type: secondaryPanelType } = useSelector(kRDrawerSecondaryPanelSelector);
  const isSecondaryPanelOpen = Boolean(secondaryPanelType);

  const safeOnChange = () => {
    // on Blur delaying the focus update for 500 milliseconds so that user click on the input right icon can be executed first . Right icon gets hidden on focus change.
    setTimeout(() => {
      setFocus(false);
    }, 500);

    if (hasError) return;

    if (localOKRName === text) return;

    onChange(localOKRName);
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Enter' && !hasError) {
      ref?.current?.blur();
    }
  };

  const handleLocalValueChange = (ev) => {
    const okrName = ev?.target?.value;
    setLocalOKRName(okrName);

    validateValue(okrName);
  };

  const validateValue = useCallback((okrName) => {
    /* check if some error is present. */
    if (okrName?.length < MIN_OKR_NAME_LENGTH) {
      setError('Cannot be empty');
      return;
    }

    if (okrName?.length > MAX_OKR_NAME_LENGTH) {
      setError(`Max ${MAX_OKR_NAME_LENGTH} characters`);
      return;
    }

    setError('');
  }, []);

  useEffect(() => {
    // firing resize event to fix the issue of input not
    // recalculating its height when secondary panel is opened
    setTimeout(() => {
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);
    }, 500);
  }, [isSecondaryPanelOpen]);

  return (
    <Box flexGrow={1} opacity={isSecondaryPanelOpen ? 0.5 : 1} pointerEvents={isSecondaryPanelOpen ? 'none' : 'unset'}>
      <InputGroup>
        {leftComponent && (
          <InputLeftElement width="18px" height="100%">
            {leftComponent}
          </InputLeftElement>
        )}

        <HeaderInput
          type={type}
          placeholder={placeholder}
          ref={ref}
          value={localOKRName}
          onChange={handleLocalValueChange}
          onKeyDown={onKeyDown}
          onBlur={safeOnChange}
          isEditable={canEditOKR && !isOKRPanelDisabled}
          onFocus={() => setFocus(true)}
          padding={leftComponent ? '4px 0px 4px 22px' : undefined}
          fontWeight="400"
          fontSize="14px"
          lineHeight="20px"
          maxHeight="50px"
        />
      </InputGroup>
      {error.length > 0 ? <Caption variant="error">{error}</Caption> : null}
    </Box>
  );
};
